import { cn } from '@/lib/utils';
import { User } from 'lucide-react';

interface UserAvatarProps {
  name: string | null | undefined;
  src: string | null | undefined;
  size?: 'sm' | 'md' | 'lg' | 'xl'; // 24 / 32 / 48 / 80 px
  /** Show a colored ring for status (e.g. red for suspended). */
  ring?: 'none' | 'red' | 'amber' | 'green';
  className?: string;
}

const SIZE = {
  sm: 'h-6 w-6 text-[10px]',
  md: 'h-8 w-8 text-xs',
  lg: 'h-12 w-12 text-sm',
  xl: 'h-20 w-20 text-lg',
} as const;

const RING = {
  none: '',
  red: 'ring-2 ring-red-500',
  amber: 'ring-2 ring-amber-500',
  green: 'ring-2 ring-emerald-500',
} as const;

function initials(name: string | null | undefined): string {
  if (!name) return '?';
  const parts = name.trim().split(/\s+/).slice(0, 2);
  return parts.map((p) => p[0]?.toUpperCase() ?? '').join('') || '?';
}

export function UserAvatar({
  name,
  src,
  size = 'md',
  ring = 'none',
  className,
}: UserAvatarProps) {
  const dim = SIZE[size];
  const ringCls = RING[ring];

  if (src) {
    return (
      <img
        src={src}
        alt={name ?? 'User'}
        className={cn(
          dim,
          ringCls,
          'rounded-full object-cover bg-muted shrink-0',
          className,
        )}
        loading="lazy"
        referrerPolicy="no-referrer"
        onError={(e) => {
          // If the public URL 404s (deleted user, broken link),
          // hide the broken image and show initials fallback
          const img = e.currentTarget as HTMLImageElement;
          img.style.display = 'none';
          // Create a fallback element next to the img
          const fallback = document.createElement('div');
          fallback.className = cn(
            dim,
            ringCls,
            'rounded-full bg-muted text-muted-foreground flex items-center justify-center font-semibold shrink-0',
            className,
          );
          fallback.textContent = initials(name);
          fallback.title = name ?? 'Unknown user';
          img.parentNode?.insertBefore(fallback, img.nextSibling);
        }}
      />
    );
  }

  return (
    <div
      className={cn(
        dim,
        ringCls,
        'rounded-full bg-muted text-muted-foreground flex items-center justify-center font-semibold shrink-0',
        className,
      )}
      title={name ?? 'Unknown user'}
      role="img"
      aria-label={name ?? 'Unknown user'}
    >
      {name ? initials(name) : <User className="h-1/2 w-1/2" />}
    </div>
  );
}
